import { HStack, Select, Skeleton, Text, createListCollection, useSelectContext } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';

import { type ReferentialKeyPublic, UtilsService } from '@/client';

const SelectValueItem = () => {
  const select = useSelectContext();
  const items = select.selectedItems as Array<{ referential: string; key: string }>;

  if (!items || items.length === 0) {
    return <Text color="fg.muted">{t('indicator.filter.externalReferentialsKeys.select')}</Text>;
  }

  return (
    <Select.ValueText placeholder={t('indicator.filter.externalReferentialsKeys.select')}>
      <HStack>
        {items.length === 1 ? (
          <Text fontWeight="medium">
            <b>{items[0].referential}</b> - {items[0].key}
          </Text>
        ) : (
          <Text fontWeight="medium">
            {items.map((item, index) => (
              <span key={index}>
                <b>{item.referential}</b> - {item.key}
                {index < items.length - 1 && ' ; '}
              </span>
            ))}
          </Text>
        )}
      </HStack>
    </Select.ValueText>
  );
};

function getReferentialsKeysKeysOptions() {
  return {
    queryKey: ['referentials_keys'],
    queryFn: async () => await UtilsService.readReferentialsKeys(),
  };
}

export const SelectReferentialsKeys = ({
  value,
  onChange,
}: {
  value: ReferentialKeyPublic[];
  onChange: (value: ReferentialKeyPublic[]) => void;
}) => {
  const { data: referentialsKeysData, isLoading } = useQuery({
    ...getReferentialsKeysKeysOptions(),
  });

  const referentialKeyCollection = createListCollection({
    items:
      referentialsKeysData?.data
        .map((referentialKey) => ({
          ...referentialKey,
          value: `${referentialKey.referential}:${referentialKey.key}`, // unique string for Select
        }))
        .sort((a, b) => {
          if (a.referential < b.referential) return -1;
          if (a.referential > b.referential) return 1;
          if (a.key < b.key) return -1;
          if (a.key > b.key) return 1;
          return 0;
        }) || [],
    itemToString: (item) => item.value,
    itemToValue: (item) => item.value,
  });

  // Convert current value to string[] for Select
  const selectedValues = value.map((item) => `${item.referential}:${item.key}`);

  return (
    <>
      {isLoading ? (
        <Skeleton height="8" />
      ) : (
        <Select.Root
          multiple
          collection={referentialKeyCollection}
          size="sm"
          value={selectedValues}
          onValueChange={(e) => {
            // Find all items matching the new selection
            const selectedItems = referentialKeyCollection.items
              .filter((el) => e.value.includes(el.value))
              .map(({ value, ...rest }) => rest);
            onChange(selectedItems);
          }}
          positioning={{ sameWidth: true }}
        >
          <Select.HiddenSelect />
          <Select.Control>
            <Select.Trigger>
              <SelectValueItem />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.ClearTrigger />
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <Select.Content>
              {referentialKeyCollection.items.map((referentialKey) => (
                <Select.Item item={referentialKey} key={referentialKey.value} justifyContent="space-between">
                  <HStack>
                    <b>{referentialKey.referential}</b> : {referentialKey.key}
                  </HStack>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Select.Root>
      )}
    </>
  );
};

export default SelectReferentialsKeys;
